import React, { useEffect, useState } from 'react';
import Box from 'ui/Box';
import { useSelector } from 'react-redux';
import { getSelectedProduct } from 'ducks/products';
import { ProductType } from '@inter-ikea-storageone/range';
import styled from 'styled-components';
import { ProductThumb, ProductThumbMobile } from 'selectedMenu/components';
import panelLayoutConfig from './panelLayoutConfig';
import { useCore } from 'contexts/core';
import PanelLayout from './types/PanelLayout';
import applyPanelLayout from './utils/applyPanelLayout';
import getPanelProductSelectionIds from './utils/getPanelProductSelectionIds';
import { CustomSlidingDoorComponent, PanelArrangement } from '@inter-ikea-storageone/range-pax';
import getPanelArrangement from './utils/getPanelArrangement';
import { isMobile } from 'utils/platform';
import { KompisCarousel, KompisCarouselItem } from '@inter-ikea-kompis/react-components';
import CarouselLayoutEnum from '@inter-ikea-kompis/component-carousel/lib/enums/CarouselLayoutEnum';
import { e2e } from 'utils/queryUtils';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { useBehaviorTracker } from 'contexts/behaviors';

const PanelLayoutPicker = styled(Box)`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  align-items: center;
`;

const CarouselWrapper = styled.div`
  max-width: 100vw;
  overflow: hidden;
  width: 100%;
  padding: 0 ${({ theme }) => theme.space.md};
`;

const PanelLayoutIcon = styled.div`
  width: ${({ theme }) => theme.space.xxxl};
  height: ${({ theme }) => theme.space.xxxxl};
  padding-bottom: ${isMobile() ? 0 : ({ theme }) => theme.space.xs};
  display: flex;
  justify-content: center;
`;

const PanelLayoutIconMobile = styled.div`
  width: 64px;
  height: 60px;
  padding-bottom: ${isMobile() ? 0 : ({ theme }) => theme.space.xs};
  display: flex;
  justify-content: center;
`;

const ChangePanelLayout = () => {
  const coreApi = useCore();
  const behaviorTracker = useBehaviorTracker();
  const selectedEntity = coreApi.entities.findSelectedEntity();
  const selectedProduct = useSelector(getSelectedProduct);
  const [selectedPanelArrangement, setSelectedPanelArrangement] = useState<PanelArrangement>(
    !selectedEntity ? PanelArrangement.Unknown : getPanelArrangement(selectedEntity, coreApi)
  );

  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSwipe = (event: any) => {
    setSelectedIndex(event.detail.selectedIndex);
  };

  const updateSelectedPanelArrangement = () => {
    if (!selectedEntity) {
      setSelectedPanelArrangement(PanelArrangement.Unknown);
      return;
    }
    setSelectedPanelArrangement(getPanelArrangement(selectedEntity, coreApi));
  };

  useEffect(() => {
    setSelectedPanelArrangement(
      !selectedEntity ? PanelArrangement.Unknown : getPanelArrangement(selectedEntity, coreApi)
    );
  }, [selectedEntity, coreApi]);

  if (
    selectedEntity === null ||
    selectedProduct?.type !== ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM
  ) {
    return null;
  }

  const onPanelLayoutClick = (panelLayout: PanelLayout) =>
    behaviorTracker.custom(() => {
      const customSlidingDoors = coreApi.entities.getByComponent(CustomSlidingDoorComponent.id);
      const slidingDoors = customSlidingDoors.length > 1 ? customSlidingDoors : [selectedEntity];
      // For custom sliding doors we change panel layout for all at once

      slidingDoors.forEach(door => {
        if (!door) return;
        const panelProductSelections = getPanelProductSelectionIds(door, coreApi);
        if (panelProductSelections === null) {
          return;
        }
        applyPanelLayout(
          door,
          panelLayout,
          panelProductSelections.primary,
          panelProductSelections.secondary,
          coreApi
        );
      });
      updateSelectedPanelArrangement();
    }, true);

  return (
    <>
      {isMobile() ? (
        <CarouselWrapper>
          <KompisCarousel
            onChange={onSwipe}
            selectedIndex={selectedIndex}
            layout={CarouselLayoutEnum.overflow}
            theme={SkapaTheme}
            showArrows={!e2e()}
          >
            {panelLayoutConfig.map((panelLayout, i) => (
              <KompisCarouselItem key={i}>
                <ProductThumbMobile
                  data-selected={panelLayout.arrangement === selectedPanelArrangement}
                  onClick={() => onPanelLayoutClick(panelLayout)}
                >
                  <PanelLayoutIconMobile>
                    <panelLayout.icon />
                  </PanelLayoutIconMobile>
                </ProductThumbMobile>
              </KompisCarouselItem>
            ))}
          </KompisCarousel>
        </CarouselWrapper>
      ) : (
        <Box mt="s">
          <PanelLayoutPicker>
            {panelLayoutConfig.map((panelLayout, i) => (
              <ProductThumb
                key={i}
                data-selected={panelLayout.arrangement === selectedPanelArrangement}
                onClick={() => onPanelLayoutClick(panelLayout)}
              >
                <PanelLayoutIcon>
                  <panelLayout.icon />
                </PanelLayoutIcon>
              </ProductThumb>
            ))}
          </PanelLayoutPicker>
        </Box>
      )}
    </>
  );
};

export default ChangePanelLayout;
